import { useIsMobile } from "@roman/shared/hooks/soccer/useIsMobile";
import type { PredictionMarketChoiceWithProbability } from "@roman/shared/types/PredictionMarket";
import { cn } from "@roman/shared/utils";
import {
  PredictionMarketType,
  getPredictionMarketType,
  getYesNoChoices,
} from "@roman/shared/utils/predictionMarket/utils";
import { MULTIPLE_COLORS } from "../../PredictionChart/utils/constants";
import { ChoiceCardGraph } from "./ChoiceCardGraph";

interface MultipleChoicesGraphProps {
  choices: PredictionMarketChoiceWithProbability[];
  outlined?: boolean;
  onlyProbability?: boolean;
  showAll?: boolean;
  selectedChoiceIds: string[];
}

export const MultipleChoicesGraph = ({ choices, showAll = false, selectedChoiceIds }: MultipleChoicesGraphProps) => {
  const predictionMarketType = getPredictionMarketType(choices.map((c) => ({ name: c.name })));

  const isMobile = useIsMobile();

  if (predictionMarketType === PredictionMarketType.YESNO) {
    const { yesChoice, noChoice } = getYesNoChoices(choices);

    const isViewYesChoiceSelected = selectedChoiceIds.some(
      (id) => choices.find((c) => c.id === id)?.name.toLowerCase() === "yes",
    );
    const isViewNoChoiceSelected = selectedChoiceIds.some(
      (id) => choices.find((c) => c.id === id)?.name.toLowerCase() === "no",
    );

    return (
      <div className="flex justify-between items-start">
        <div className="flex items-end w-full py-1 gap-9">
          {isViewYesChoiceSelected && (
            <div className="flex items-center gap-1 font-medium text-sm">
              <span className="w-[10px] h-[10px] rounded-full bg-chart-1" />
              <span className="uppercase text-sm leading-[140%] text-dark-gray font-normal">{yesChoice.name}</span>
              <span className="text-dark-gray font-sans font-bold text-lg leading-[140%]">
                {Math.round(yesChoice.probability * 100)}%
              </span>
            </div>
          )}
          {isViewNoChoiceSelected && (
            <div className="flex items-center gap-1 font-medium text-sm">
              <span className="w-[10px] h-[10px] rounded-full bg-chart-2" />
              <span className="uppercase text-sm leading-[140%] text-dark-gray font-normal">{noChoice.name}</span>
              <span className="text-dark-gray font-sans font-bold text-lg leading-[140%]">
                {Math.round(noChoice.probability * 100)}%
              </span>
            </div>
          )}
        </div>
        <img
          src="/assets/branch_text.png"
          alt="Branch text"
          className={isMobile ? "w-[85px] h-[33px]" : "h-[40px] w-[100px]"}
        />
      </div>
    );
  }

  const displayedChoices = choices
    .filter((choice) => selectedChoiceIds.includes(choice.id))
    .slice(0, 4)
    .map((choice) => choice.id);

  return (
    <div className="flex justify-between items-start">
      <div className="flex w-full justify-between items-start">
        <div className={cn("grid gap-1 overflow-y-scroll hide-scrollbar flex-3", showAll ? "h-full" : "h-[90px]")}>
          {choices.map((choice, index) => (
            <div className={cn(!displayedChoices.includes(choice.id) ? "hidden" : "")}>
              <ChoiceCardGraph
                key={choice.id}
                id={choice.id}
                name={choice.name}
                probability={choice.probability}
                color={MULTIPLE_COLORS[index % MULTIPLE_COLORS.length]}
                onClick={() => {}}
              />
            </div>
          ))}
        </div>
        <img
          src="/assets/branch_text.png"
          alt="Branch text"
          className={isMobile ? "w-[85px] h-[33px]" : "h-[40px] w-[100px]"}
        />
      </div>
    </div>
  );
};
